import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, CheckCircle2, AlertTriangle, FileText, Send, ListChecks, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ai, useAT } from "@/lib/aiApi";

const LEVEL = { high: "text-red-300 border-red-500/30", medium: "text-amber-300 border-amber-500/30", low: "text-slate-300 border-slate-600" };
const STATUS = { received: "text-green-300", missing: "text-red-300", check: "text-amber-300" };
const err = (e) => toast.error(e?.response?.data?.detail?.toString?.() || "Error");

const Done = ({ a }) => {
  const at = useAT();
  return a ? <span className="inline-flex items-center gap-1 text-xs text-green-300"><CheckCircle2 className="h-3.5 w-3.5" />{at("done")} · {at("by")} {a.by}</span> : null;
};

export const AiAgentPanel = ({ processId, onChanged }) => {
  const at = useAT();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await ai.forCase(processId);
      setData(d);
      const cur = d.drafts?.find((x) => x.status !== "discarded");
      if (cur?.status === "ready") setMsg((m) => m || cur.output?.customer_message?.text || "");
      clearTimeout(timer.current);
      if (cur?.status === "running") timer.current = setTimeout(load, 2000);
    } catch (e) { setData({ drafts: [] }); }
  }, [processId]);
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);

  if (!data) return null;
  const draft = data.drafts?.find((x) => x.status !== "discarded");
  const out = draft?.output;
  const acts = draft?.actions || {};
  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); if (okMsg) toast.success(okMsg(r)); await load(); onChanged?.(key); } catch (e) { err(e); } finally { setBusy(""); }
  };

  return (
    <section id="ai" className="rounded-2xl border border-fuchsia-500/20 bg-gradient-to-br from-fuchsia-500/5 to-slate-900/40 p-5 space-y-4" data-testid="case-ai">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Sparkles className="h-5 w-5 text-fuchsia-300" />{data.agent || at("title")}</h2>
        {data.agent && (
          <Button size="sm" className="bg-fuchsia-500 text-white hover:bg-fuchsia-400" disabled={draft?.status === "running" || busy === "prep"}
            onClick={() => act("prep", () => ai.prepare(processId), null)} data-testid="ai-prepare">
            {draft?.status === "running" || busy === "prep" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
            {out ? at("again") : at("prepare")}
          </Button>
        )}
      </div>
      {!data.agent && <p className="text-sm text-slate-400">{at("none")}</p>}
      {draft?.status === "running" && <p className="text-sm text-fuchsia-200">{at("preparing")}</p>}

      {out && (
        <div className="space-y-4" data-testid="ai-draft">
          <p className="text-xs text-slate-400">{at("review")} · {at("provider")}: {draft.provider} · {(draft.finished_at || "").slice(0, 16).replace("T", " ")}</p>
          {draft.error && <p className="text-xs text-amber-300">{at("fallback")} ({draft.error.slice(0, 120)})</p>}

          <div><h3 className="text-xs uppercase tracking-wider text-slate-500 mb-1">{at("summary")}</h3>
            <p className="whitespace-pre-wrap text-sm text-slate-200">{out.summary}</p></div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs uppercase tracking-wider text-slate-500">{at("checklist")}</h3>
                {acts.checklist ? <Done a={acts.checklist} /> : (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-slate-700 bg-slate-900" disabled={busy === "docs"}
                    onClick={() => act("docs", () => ai.addDocuments(draft.id), (r) => `+${r.added}`)} data-testid="ai-add-docs"><FileText className="h-3 w-3 mr-1" />{at("addDocs")}</Button>
                )}
              </div>
              <ul className="space-y-1 text-sm">
                {out.checklist.map((c, i) => (
                  <li key={i} className="flex justify-between gap-3 border-b border-slate-800/60 py-1">
                    <span>{c.item}{c.note ? <span className="text-xs text-slate-500"> — {c.note}</span> : null}</span>
                    <span className={`text-xs whitespace-nowrap ${STATUS[c.status] || ""}`}>{at(c.status) || c.status}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-xs uppercase tracking-wider text-slate-500">{at("risks")}</h3>
                  {acts.tasks ? <Done a={acts.tasks} /> : (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-slate-700 bg-slate-900" disabled={busy === "tasks"}
                      onClick={() => act("tasks", () => ai.addTasks(draft.id), (r) => `+${r.added}`)} data-testid="ai-add-tasks"><ListChecks className="h-3 w-3 mr-1" />{at("addTasks")}</Button>
                  )}
                </div>
                {out.risks.length === 0 && <p className="text-sm text-slate-500">—</p>}
                {out.risks.map((r, i) => (
                  <p key={i} className={`mb-1 rounded-lg border px-2 py-1 text-sm ${LEVEL[r.level] || LEVEL.low}`}><AlertTriangle className="inline h-3.5 w-3.5 mr-1" />{r.text}</p>
                ))}
              </div>
              {out.next_steps?.length > 0 && (
                <div><h3 className="text-xs uppercase tracking-wider text-slate-500 mb-1">{at("nextSteps")}</h3>
                  <ul className="list-disc pl-5 text-sm text-slate-300">{out.next_steps.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-xs uppercase tracking-wider text-slate-500">{at("proposal")}</h3>
                  {acts.proposal ? <Done a={acts.proposal} /> : (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-slate-700 bg-slate-900" disabled={busy === "prop" || !out.proposal.lines.length}
                      onClick={() => act("prop", () => ai.toProposal(draft.id), (r) => (r.unpriced?.length ? `${at("unpriced")}: ${r.unpriced.join(", ")}` : at("done")))} data-testid="ai-to-proposal">
                      <Receipt className="h-3 w-3 mr-1" />{at("toProposal")}</Button>
                  )}
                </div>
                <p className="text-sm font-medium">{out.proposal.title}</p>
                <ul className="text-sm text-slate-300">{out.proposal.lines.map((l, i) => <li key={i}>• {l.code} × {l.qty}{l.note ? <span className="text-xs text-slate-500"> — {l.note}</span> : null}</li>)}</ul>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs uppercase tracking-wider text-slate-500">{at("message")} ({out.customer_message.language})</h3>
              <Done a={acts.message} />
            </div>
            <Textarea rows={8} className="bg-slate-950 border-slate-700 text-sm" value={msg} onChange={(e) => setMsg(e.target.value)} disabled={!!acts.message} data-testid="ai-message" />
            {!acts.message && (
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" className="bg-venka-green text-slate-950 hover:bg-venka-green-light" disabled={busy === "msg" || !msg.trim()}
                  onClick={() => act("msg", () => ai.sendMessage(draft.id, msg), () => at("done"))} data-testid="ai-send-message"><Send className="h-3.5 w-3.5 mr-1" />{at("sendMsg")}</Button>
                <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => act("discard", () => ai.discard(draft.id), null)}><Trash2 className="h-3.5 w-3.5 mr-1" />{at("discard")}</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default AiAgentPanel;
